import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, LinkIcon, Calendar, MapPin, Award, Users, Clock, CheckCircle, AlertCircle, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WorkshopsPage = () => {
  const navigate = useNavigate();
  const [notificationBanner, setNotificationBanner] = useState(null);
  const [showBanner, setShowBanner] = useState(true);

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const response = await axios.get(`${API}/settings/notification-banner`);
        setNotificationBanner(response.data);
        setShowBanner(response.data.is_active);
      } catch (err) {
        setNotificationBanner({
          text: "🎉 EvalentX 2nd Edition registrations are NOW OPEN!",
          is_active: true
        });
      }
    };
    fetchBanner();
  }, []);

  const workshops = [
    {
      id: 1,
      title: 'Artificial Intelligence',
      icon: <Brain size={48} />,
      color: 'red',
      description: 'Master the fundamentals of AI and machine learning. Learn about neural networks, deep learning, and practical AI applications.',
      topics: [
        'Introduction to Machine Learning',
        'Neural Networks & Deep Learning',
        'Computer Vision Basics',
        'Natural Language Processing',
        'AI Model Deployment'
      ],
      duration: '5 hours',
      level: 'Beginner to Intermediate'
    },
    {
      id: 2,
      title: 'Blockchain',
      icon: <LinkIcon size={48} />,
      color: 'black',
      description: 'Explore the world of decentralized technologies. Learn about blockchain architecture, smart contracts, and Web3 development.',
      topics: [
        'Blockchain Fundamentals & Architecture',
        'Smart Contracts Development',
        'Ethereum & Solidity Basics',
        'Decentralized Applications (dApps)',
        'Web3 Integration & Tools'
      ],
      duration: '5 hours',
      level: 'Beginner to Intermediate'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Dynamic Notification Bar */}
      {showBanner && notificationBanner && notificationBanner.is_active && (
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white py-3 px-4 md:px-6 relative">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 md:gap-3">
            <AlertCircle size={18} className="flex-shrink-0 hidden sm:block" />
            <p className="text-xs sm:text-sm md:text-base font-semibold text-center leading-tight pr-8">
              {notificationBanner.text}
            </p>
            <button
              onClick={() => setShowBanner(false)}
              className="absolute right-4 md:right-6 top-1/2 transform -translate-y-1/2 text-white/80 hover:text-white transition-colors"
              aria-label="Close notification"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative bg-black text-white py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-red-900/20 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-black mb-6">
            Technical <span className="text-red-600">Workshops</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8">
            Expert-Led 5-Hour Sessions by Industry Professionals
          </p>
          <div className="flex flex-col md:flex-row justify-center items-center gap-8 text-lg">
            <div className="flex items-center gap-3 bg-white/10 px-6 py-3 rounded-lg">
              <Calendar className="text-red-400" size={24} />
              <span className="font-semibold">11th April 2026</span>
            </div>
            <div className="flex items-center gap-3 bg-white/10 px-6 py-3 rounded-lg">
              <MapPin className="text-red-400" size={24} />
              <span className="font-semibold">IIT Gandhinagar</span>
            </div>
          </div>
        </div>
      </section>

      {/* Workshops Grid */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {workshops.map((workshop, index) => (
              <div
                key={workshop.id}
                className="group bg-white border-2 border-gray-200 hover:border-red-600 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all"
              >
                <div className="flex flex-col md:flex-row gap-8">
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-24 h-24 rounded-xl flex items-center justify-center ${
                    workshop.color === 'red' ? 'bg-red-600' : 'bg-black'
                  } text-white group-hover:scale-110 transition-transform`}>
                    {workshop.icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h2 className="text-3xl font-black text-black mb-3">
                      {workshop.title}
                    </h2>
                    <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                      {workshop.description}
                    </p>
                    
                    <div className="grid md:grid-cols-2 gap-8 mb-6">
                      {/* Topics */}
                      <div>
                        <h3 className="font-bold text-xl text-black mb-4">Topics Covered:</h3>
                        <ul className="space-y-3">
                          {workshop.topics.map((topic, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <CheckCircle className="text-red-600 flex-shrink-0 mt-1" size={20} />
                              <span className="text-gray-700">{topic}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Details */}
                      <div className="space-y-4">
                        <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                          <div className="flex items-center gap-3 mb-2">
                            <Clock className="text-red-600" size={20} />
                            <span className="font-bold text-black">Duration</span>
                          </div>
                          <p className="text-gray-700 ml-8">{workshop.duration}</p>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                          <div className="flex items-center gap-3 mb-2">
                            <Users className="text-red-600" size={20} />
                            <span className="font-bold text-black">Level</span>
                          </div>
                          <p className="text-gray-700 ml-8">{workshop.level}</p>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                          <div className="flex items-center gap-3 mb-2">
                            <Award className="text-red-600" size={20} />
                            <span className="font-bold text-black">Certificate</span>
                          </div>
                          <p className="text-gray-700 ml-8">Included</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-black via-red-900 to-black text-white">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="text-4xl md:text-5xl font-black mb-6">
            Ready to <span className="text-red-400">Level Up</span> Your Skills?
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Join expert-led workshops and gain hands-on experience with cutting-edge technologies
          </p>
          <button
            onClick={() => navigate('/register')}
            className="bg-white hover:bg-red-600 text-black hover:text-white px-12 py-5 rounded-full font-black text-xl transition-all transform hover:scale-110 shadow-2xl"
          >
            Register Now
          </button>
        </div>
      </section>
    </div>
  );
};

export default WorkshopsPage;
